import React from 'react';
import { useTranslation } from 'common/i18n/hooks';
import { MultiMetricsQueryBlock } from 'common/query-builder/query-block/multi-metrics-menu/query-block';
import type { BehaviorMenuItem, MenuTypeMap, MultiMetricsSectionItem } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { MenuType, MenuTypeToMetricType } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { useMetricEntries } from 'common/report/insights/contexts/metrics';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import { getNewMetricEntry } from 'iron/common/query-builder/query-entry-section/row-types/multi-metrics-entry/metric-helper';
import type { RowProps } from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import { ResourceType } from 'common/report/constants';
import { findKeyByValue } from 'common/util/typescript';

/** Selecting a Behavior inside a metric is restricted to the following types */
type ValidBehaviorSelections = MenuType.Simple | MenuType.Funnel | MenuType.Retention | MenuType.Profile | MenuType.Cohort;

/**
 * Component for selecting a behavior: retention, funnel, simple behaviors or cohort selection
 * This component directly modifies the bookmark by using the `useMetricEntries` hook
 */
export function BehaviorSelector(props: RowProps) {
  const {
    t
  } = useTranslation();
  const {
    selectMetricItem
  } = useMetricEntries();
  const label = props.entry.behavior.name || t("query-builder.metrics.new-definition");
  const handleBehaviorSelection = (selectedItem: MultiMetricsSectionItem) => {
    var _ref, _props$entry$behavior;
    selectMetricItem({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      dataGroupId: (_ref = (_props$entry$behavior = props.entry.behavior.dataGroupId) !== null && _props$entry$behavior !== void 0 ? _props$entry$behavior : props.entry.measurement.dataGroupId) !== null && _ref !== void 0 ? _ref : null,
      selectedItem
    });
  };
  const handleBehaviorCreate = (menuType: MenuType) => {
    var _props$sectionProps$e;
    const entry = getNewMetricEntry({
      menuType,
      entries: (_props$sectionProps$e = props.sectionProps.entries) !== null && _props$sectionProps$e !== void 0 ? _props$sectionProps$e : []
    });
    props.updateEntry(entry);
  };
  const excludedMetricTypes = Object.values(BookmarkTypes.MetricType).filter(entry => entry !== props.entry.behavior.type);
  let selected: MenuTypeMap[ValidBehaviorSelections][`value`];
  if (props.entry.behavior.type === BookmarkTypes.MetricType.Cohort) {
    var _props$entry$behavior2;
    selected = props.entry.behavior.id ? {
      resourceType: ResourceType.Cohort,
      value: props.entry.behavior.id,
      label,
      dataGroupId: (_props$entry$behavior2 = props.entry.behavior.dataGroupId) !== null && _props$entry$behavior2 !== void 0 ? _props$entry$behavior2 : ""
    } : {
      resourceType: ResourceType.Cohort,
      label,
      value: {
        cohort: {
          raw_cohort: props.entry.behavior.raw_cohort
        }
      }
    };
  } else if (props.entry.behavior.type === BookmarkTypes.MetricType.People) {
    selected = "$all_people";
  } else {
    var _props$entry$behavior3, _props$entry$behavior4;
    selected = {
      id: (_props$entry$behavior3 = props.entry.behavior.id) !== null && _props$entry$behavior3 !== void 0 ? _props$entry$behavior3 : undefined,
      name: (_props$entry$behavior4 = props.entry.behavior.name) !== null && _props$entry$behavior4 !== void 0 ? _props$entry$behavior4 : ""
    } as BehaviorMenuItem[`value`];
  }
  return <MultiMetricsQueryBlock readonly={props.entry.behavior.type === BookmarkTypes.MetricType.People} projectHasCohorts selected={{
    menuType: findKeyByValue(MenuTypeToMetricType, props.entry.behavior.type),
    label,
    value: selected
  }} contextPanelParentElement={props.contextPanelParentElement} excludedMetricsRowTypes={excludedMetricTypes} allDataGroups={props.sectionProps.dataGroupsEnabled} dataGroupId={props.sectionProps.dataGroupId} hideEditControls={props.sectionProps.hideEditControls} queryOrigin={props.sectionProps.queryOrigin} showSessionEvent={props.sectionProps.showSessions} supportB2bCohorts={props.sectionProps.supportB2bCohorts} trackName={props.sectionProps.trackName} projectId={String(props.sectionProps.projectId)} workspaceId={String(props.sectionProps.workspaceId)} userId={Number(props.sectionProps.userId)} onSelect={handleBehaviorSelection} onCreate={(ev: {
    detail: {
      item: MenuType;
    };
  }) => handleBehaviorCreate(ev.detail.item)}
  /** @todo(metrics) figure out tracking */ onTrack={() => null} onClose={() => null} onOpen={() => null} data-sentry-element="MultiMetricsQueryBlock" data-sentry-component="BehaviorSelector" data-sentry-source-file="behavior-selector.tsx" />;
}